import React, { useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Contact from '@/components/Contact';
import FAQ from '@/components/FAQ';
import Footer from '@/components/Footer';
import { Arrow } from '@/components/Hero';
import useReveal from '@/hooks/useReveal';
import { updatePageMeta } from '@/lib/seo';
import SERVICES from '@/data/services';

const ENGAGEMENT = [
  { label: 'Pricing', value: 'Fixed, quoted after one scoping call' },
  { label: 'Timeline', value: 'Most projects live in three to six weeks' },
  { label: 'Your stack', value: 'Built on the tools you already use' },
  { label: 'Point of contact', value: 'One, from scoping to support' },
];

const ServicePage = ({ service }) => {
  const canonical = `https://rapidrune.com/services/${service.slug}`;

  useEffect(() => {
    updatePageMeta({
      title: service.metaTitle,
      description: service.metaDescription,
      canonical,
    });
  }, [service, canonical]);

  useReveal();

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    url: canonical,
    description: service.metaDescription,
    areaServed: 'United States',
    provider: {
      '@type': 'ProfessionalService',
      name: 'Rapid Rune',
      url: 'https://rapidrune.com/',
      email: 'inquire@rapidrune.com',
    },
  };

  const others = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <Navigation />
      <main className="service-page" data-testid={`service-page-${service.slug}`}>
        <section className="service-hero">
          <div className="wrap service-hero-grid">
            <div className="service-hero-copy">
              <h1 className="reveal">{service.heroTitle}</h1>
              <p className="lead reveal reveal-1">{service.intro}</p>
              <div className="hero-actions reveal reveal-2">
                <a href="#contact" className="btn btn-accent" data-testid="service-cta-primary">
                  Start a project
                  <Arrow />
                </a>
                <a href="/#services" className="link-arrow" data-testid="service-cta-secondary">
                  All services
                </a>
              </div>
            </div>
            <aside className="engagement reveal reveal-2" aria-label="How an engagement works">
              <h2>How an engagement works</h2>
              <dl>
                {ENGAGEMENT.map((e) => (
                  <div key={e.label}>
                    <dt>{e.label}</dt>
                    <dd>{e.value}</dd>
                  </div>
                ))}
              </dl>
              <a href="#contact" className="btn btn-accent">
                Start a project
                <Arrow />
              </a>
            </aside>
          </div>
        </section>

        <section className="section section-alt" data-testid="service-includes">
          <div className="wrap">
            <header className="section-head">
              <h2 className="reveal">Included in every engagement.</h2>
              <p className="lead reveal reveal-1">
                Fixed scope and a fixed price, agreed after one scoping call. Most projects go live in three to six weeks.
              </p>
            </header>
            <ul className="reasons">
              {service.includes.map((item, i) => (
                <li key={item.label} className={`reveal reveal-${(i % 3) + 1}`}>
                  <span className="step-lamp" aria-hidden="true" />
                  <h3>{item.label}</h3>
                  <p>{item.desc}</p>
                </li>
              ))}
            </ul>
            <nav className="service-others reveal" aria-label="Other services">
              <h2>Other services</h2>
              <ul>
                {others.map((o) => (
                  <li key={o.slug}>
                    <a href={`/services/${o.slug}`}>
                      {o.name}
                      <Arrow />
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </section>

        <FAQ faqs={service.faqs} testId="service-faq" />

        <Contact />
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </>
  );
};

export default ServicePage;
